import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { icons } from "../../../icons";
import { HeadLine3 } from "../../typography/styled";
import Button from "../../button";
import Input from "../../input";
import Select from "../../select";
import Toggle from "../../toggle";
import Counter from "../../counter";
import { kubernetesService } from "../../../api";
import { instanceService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
  HideContainer,
  DynamicRowsContainer,
  DynamicRow,
  RemoveButton,
  SectionLabel,
} from "./styles";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: (data?: any, setErrors?: any) => void;
  alertTitle?: string;
  dataToEdit?: any;
  loading?: boolean;
};

const EFFECT_OPTIONS = [
  { value: "NoSchedule", label: "NoSchedule" },
  { value: "PreferNoSchedule", label: "PreferNoSchedule" },
  { value: "NoExecute", label: "NoExecute" },
];

const CreateNodePool: FC<ModalProps> = (props) => {
  const { closeModal, closeModalCancel, dataToEdit } = props;
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [loading, setLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [autoscale, setAutoscale] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [flavors, setFlavors] = useState<any[]>([]);
  const [labels, setLabels] = useState<{ key: string; value: string }[]>([]);
  const [taints, setTaints] = useState<
    { key: string; value: string; effect: string }[]
  >([]);

  useEffect(() => {
    instanceService
      .getFlavorList({ page_size: 200 })
      .then((res) => {
        const list = res.data?.objects || res.data?.results || [];
        setFlavors(
          list.map((f: any) => ({
            value: f.id,
            label: `${f.name} (${f.vcpus} vCPU, ${f.memory_mb || f.ram}MB)`,
          }))
        );
      })
      .catch(() => {});
  }, []);

  const onSubmit = (values: any) => {
    setLoading(true);
    const data = {
      name: values.name,
      flavor_id: values.flavor_id,
      count: +values.count,
      autoscale,
      max_count: autoscale ? +values.max_count : null,
      is_default: isDefault,
      labels,
      taints: taints.map((t) => ({
        key: t.key,
        value: t.value,
        effect: t.effect,
      })),
      cluster_id: dataToEdit?.cluster_id,
    };
    kubernetesService
      .createNodePool(data)
      .then(() => {
        setLoadingComplete(true);
        setTimeout(() => {
          closeModal(data);
          setLoading(false);
          setLoadingComplete(false);
        }, 1500);
      })
      .catch((err) => {
        setLoading(false);
        viewAlert({
          severity: "error",
          message:
            err?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
        });
      });
  };

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(`${t("REQUIRED_FIELD")}`),
      flavor_id: yup.string().required(`${t("REQUIRED_FIELD")}`),
      count: yup
        .number()
        .required(`${t("REQUIRED_FIELD")}`)
        .min(1, `${t("MIN_COUNT_NUMBER")} 1`),
      max_count: yup.number().when([], {
        is: () => autoscale,
        then: (s) =>
          s
            .required(`${t("REQUIRED_FIELD")}`)
            .min(1, `${t("MIN_COUNT_NUMBER")} 1`),
        otherwise: (s) => s.nullable(),
      }),
    }),
    initialValues: {
      name: "",
      flavor_id: "",
      count: 1,
      max_count: 1,
    },
    onSubmit,
  });

  const formikProps = { values, errors, touched, handleChange, handleSubmit };

  const addLabel = () => setLabels([...labels, { key: "", value: "" }]);
  const removeLabel = (idx: number) =>
    setLabels(labels.filter((_, i) => i !== idx));
  const updateLabel = (idx: number, field: "key" | "value", val: string) => {
    const updated = [...labels];
    updated[idx][field] = val;
    setLabels(updated);
  };

  const addTaint = () =>
    setTaints([...taints, { key: "", value: "", effect: "NoSchedule" }]);
  const removeTaint = (idx: number) =>
    setTaints(taints.filter((_, i) => i !== idx));
  const updateTaint = (
    idx: number,
    field: "key" | "value" | "effect",
    val: string
  ) => {
    const updated = [...taints];
    updated[idx][field] = val;
    setTaints(updated);
  };

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: "10px", marginTop: "10px" }}>
          {t("CREATE_NODE_POOL")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <Input
          title={t("NAME")}
          fieldName="name"
          value={values.name}
          handleChange={handleChange}
          errors={errors}
          touched={touched}
          required
        />
        <Select
          title={t("FLAVOR")}
          data={flavors}
          selectedValue={values.flavor_id}
          onChange={(val: any) => setFieldValue("flavor_id", val)}
          fieldName="flavor_id"
          errors={errors}
          touched={touched}
          hasSearch
          required
        />
        <Counter
          title={t("COUNT")}
          value={values.count}
          minCount={1}
          hideItems={`${t("ITEMS")}`}
          onChange={(v: any) => handleChange("count")(v.toString())}
          fieldName="count"
          {...formikProps}
        />
        <Toggle
          title={t("AUTOSCALE")}
          value={autoscale}
          onChange={() => setAutoscale(!autoscale)}
        />
        <HideContainer active={autoscale}>
          <Counter
            title={t("MAX_COUNT")}
            value={values.max_count}
            minCount={1}
            hideItems={`${t("ITEMS")}`}
            onChange={(v: any) => handleChange("max_count")(v.toString())}
            fieldName="max_count"
            {...formikProps}
          />
        </HideContainer>
        <Toggle
          title={t("IS_DEFAULT")}
          value={isDefault}
          onChange={() => setIsDefault(!isDefault)}
        />

        <SectionLabel>{t("LABELS")}</SectionLabel>
        <DynamicRowsContainer>
          {labels.map((label, idx) => (
            <DynamicRow key={idx}>
              <Input
                title={t("KEY")}
                value={label.key}
                handleChange={(e: any) =>
                  updateLabel(idx, "key", e.target.value)
                }
              />
              <Input
                title={t("VALUE")}
                value={label.value}
                handleChange={(e: any) =>
                  updateLabel(idx, "value", e.target.value)
                }
              />
              <RemoveButton
                src={icons.CloseIcon}
                onClick={() => removeLabel(idx)}
              />
            </DynamicRow>
          ))}
          <Button
            variant="stroke"
            size="small"
            onClick={addLabel}
            title={`+ ${t("ADD")}`}
          />
        </DynamicRowsContainer>

        <SectionLabel>{t("TAINTS")}</SectionLabel>
        <DynamicRowsContainer>
          {taints.map((taint, idx) => (
            <DynamicRow key={idx}>
              <Input
                title={t("KEY")}
                value={taint.key}
                handleChange={(e: any) =>
                  updateTaint(idx, "key", e.target.value)
                }
              />
              <Input
                title={t("VALUE")}
                value={taint.value}
                handleChange={(e: any) =>
                  updateTaint(idx, "value", e.target.value)
                }
              />
              <Select
                title={t("EFFECT")}
                data={EFFECT_OPTIONS}
                selectedValue={taint.effect}
                onChange={(val: any) => updateTaint(idx, "effect", val)}
              />
              <RemoveButton
                src={icons.CloseIcon}
                onClick={() => removeTaint(idx)}
              />
            </DynamicRow>
          ))}
          <Button
            variant="stroke"
            size="small"
            onClick={addTaint}
            title={`+ ${t("ADD")}`}
          />
        </DynamicRowsContainer>

        <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
          <Button
            variant="stroke"
            size="display"
            customStyles={{ width: "177px", justifyContent: "center" }}
            onClick={closeModalCancel}
            title={t("CANCEL")}
          />
          <Button
            variant="primary"
            size="display"
            loading={loading}
            loadingComplete={loadingComplete}
            customStyles={{ width: "177px", justifyContent: "center" }}
            onClick={handleSubmit}
            title={t("CREATE")}
          />
        </div>
      </BodyModal>
    </Container>
  );
};

export default CreateNodePool;
